<script setup lang="ts">
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import type TripPackage from '@itinerary/trip/TripPackage';

interface IProps {
    tripPackage: TripPackage;
    isSelected?: boolean;
}
const { tripPackage, isSelected } = defineProps<IProps>();
const emit = defineEmits<{
    reserve: [];
}>();
const reserveText = computed(() =>
    /\d/.test(tripPackage.buyText) ? 'رزرو آنلاین' : tripPackage.buyText
);

const buttonColorVariant = computed(() =>
    tripPackage.autoReserveEnabled ? 'secondary' : 'primary'
);
const reserveButtonIcon = computed(() =>
    tripPackage.reservable && !isSelected ? faArrowLeft : undefined
);

// why this calculate here and not in the priceclass
const discountPercent = computed(() =>
    !tripPackage.reservable
        ? 0
        : Math.round(
              ((tripPackage.singlePrice - tripPackage.singlePayable) /
                  tripPackage.singlePrice) *
                  1000
          ) / 10
);
</script>

<template>
    <div class="pricing-container">
        <mr-price-re
            v-if="discountPercent"
            :price="tripPackage.singlePrice"
            :percent="discountPercent"
            type="discount"
            class="discount"
        />
        <mr-typography
            v-else-if="tripPackage.priceDescription && tripPackage.reservable"
            size="x-small"
            color="var(--cool-gray-700)"
        >
            ({{ tripPackage.priceDescription }})
        </mr-typography>
        <mr-price-re
            v-if="tripPackage.singlePayable && !tripPackage.autoReserveEnabled"
            class="payable-price package-price"
            :class="{
                selected: isSelected || !tripPackage.reservable,
            }"
            :price="tripPackage.singlePayable"
            :type="{ mobile: 'small' }"
        />
        <mr-button
            v-if="!isSelected && tripPackage.showReserveButton"
            full-width
            class="reserve-button"
            size="big"
            :left-icon="reserveButtonIcon"
            :disabled="
                !tripPackage.reservable && !tripPackage.autoReserveEnabled
            "
            :color="buttonColorVariant"
            @click.stop="emit('reserve')"
        >
            <mr-typography
                size="large"
                weight="700"
                color="inherit"
                white-space="nowrap"
            >
                {{ reserveText }}
            </mr-typography>
        </mr-button>
    </div>
</template>

<style lang="scss" scoped>
.pricing-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    justify-content: flex-start;
    @include desktop-only {
        width: 100%;
        align-items: flex-start;
    }
    .reserve-button {
        @include mobile-only() {
            display: none;
        }
    }
    .payable-price {
        height: 20px;
    }

    .discount {
        :deep(.price) {
            @include tg-s($w5);
        }

        :deep(.discount) {
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 6px !important;
            @include tg-s();
        }
    }
    .package-price {
        :deep(.price) {
            @include tg-xl($w7);
        }
        :deep(.unit) {
            margin-right: 2px !important;
            @include tg-xl($w5);
        }
    }
}
</style>
